import React from 'react'
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	ListSubheader,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { mainBlue, mainGreen } from '../../../utils/color'
import { transformDetailWithPhone } from '../../../utils/transformDetail'
import {
	accountTypeLabel,
	categoryAllowedSources,
	categoryAllowsOrders,
	categoryAllowsSource,
	categoryGroupSx,
	DEFAULT_CASH_SOURCE,
	expenseSourceLabel,
	formatMoney,
	orderAssignmentRowSx,
	orderCode,
	orderContactText,
	orderItemsText,
	parseMoney,
	SHIPPING_EXPENSE_NAME,
} from '../cashflowUtils'
import { OrderPickerPopper } from './CashflowCommon'

export default function ExpenseDialog({
	bankAccounts,
	canManageExpense,
	cashoutExpenseCategories = [],
	closeExpenseDialog,
	editingExpenseId,
	expenseDialogOpen,
	expenseForm,
	expenseOrderOptions,
	getLockedOrderExpense,
	otherExpenseCategories,
	saveExpense,
	selectedExpenseCategory,
	selectedExpenseOrders,
	selectedShippingIncome,
	setExpenseForm,
	shippingExpenseCategories,
}) {
	const isShippingExpense = expenseForm.type === 'shipping'
	const bankOnlyAccounts = bankAccounts.filter((account) => (account.accountType || 'bank') === 'bank')
	const canPickOrders =
		isShippingExpense || Boolean(selectedExpenseCategory && categoryAllowsOrders(selectedExpenseCategory))
	const expenseKindValue = expenseForm.category
	const allowsCashSource = categoryAllowsSource(selectedExpenseCategory, 'cash')
	const allowsBankSource = categoryAllowsSource(selectedExpenseCategory, 'bank')

	// Đổi khoản chi có thể làm tài khoản đang chọn không còn hợp lệ, phải chọn lại nguồn phù hợp
	const nextBankAccountForCategory = (category, currentBankAccount) => {
		const usesCash = currentBankAccount === DEFAULT_CASH_SOURCE
		if (categoryAllowsSource(category, usesCash ? 'cash' : 'bank')) return currentBankAccount
		return categoryAllowsSource(category, 'cash') ? DEFAULT_CASH_SOURCE : ''
	}

	const updateExpenseKind = (value) => {
		const categories = [...shippingExpenseCategories, ...otherExpenseCategories, ...cashoutExpenseCategories]
		const category = categories.find((item) => item._id === value)
		setExpenseForm((prev) => {
			const shouldSyncContent =
				!prev.content || prev.content === selectedExpenseCategory?.name || prev.content === SHIPPING_EXPENSE_NAME
			const nextType = category?.type || 'other'
			return {
				...prev,
				type: nextType,
				category: value,
				bankAccount: nextBankAccountForCategory(category, prev.bankAccount),
				orderIds: nextType === 'shipping' || (category && categoryAllowsOrders(category)) ? prev.orderIds : [],
				content: shouldSyncContent ? category?.name || '' : prev.content,
			}
		})
	}

	return (
		<Dialog
			open={expenseDialogOpen}
			onClose={closeExpenseDialog}
			fullWidth
			maxWidth='md'
			PaperProps={{ className: 'cashflow-dialog-paper' }}>
			<DialogTitle>{editingExpenseId ? 'Sửa khoản chi' : 'Tạo khoản chi'}</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={1.75} sx={{ pt: 0.5 }}>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
						<Box sx={{ flex: 1 }}>
							<FormControl fullWidth size='small'>
								<InputLabel>Tài khoản chi</InputLabel>
								<Select
									label='Tài khoản chi'
									value={expenseForm.bankAccount}
									onChange={(event) => setExpenseForm((prev) => ({ ...prev, bankAccount: event.target.value }))}>
									<MenuItem value='' disabled sx={{ color: '#637381', fontStyle: 'italic' }}>
										Chọn tài khoản chi
									</MenuItem>
									{allowsCashSource && <MenuItem value={DEFAULT_CASH_SOURCE}>Tiền mặt mặc định</MenuItem>}
									{allowsBankSource &&
										bankOnlyAccounts.map((account) => (
											<MenuItem key={account._id} value={account._id}>
												{account.name} - {accountTypeLabel(account)}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						</Box>
						<Box sx={{ width: { xs: '100%', sm: 240 } }}>
							<TextField
								fullWidth
								size='small'
								label='Số tiền'
								value={expenseForm.amount}
								onChange={(event) => setExpenseForm((prev) => ({ ...prev, amount: event.target.value }))}
							/>
						</Box>
					</Stack>
					<Box>
						<FormControl fullWidth size='small'>
							<InputLabel>Khoản chi</InputLabel>
							<Select
								label='Khoản chi'
								value={expenseKindValue}
								onChange={(event) => updateExpenseKind(event.target.value)}>
								<MenuItem value='' disabled sx={{ color: '#637381', fontStyle: 'italic' }}>
									Chọn khoản chi
								</MenuItem>
								{shippingExpenseCategories.length > 0 && (
									<ListSubheader disableSticky sx={categoryGroupSx('shipping')}>
										Chi ship
									</ListSubheader>
								)}
								{shippingExpenseCategories.map((category) => (
									<MenuItem key={category._id} value={category._id}>
										<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
											<Typography>{category.name}</Typography>
											<Chip
												size='small'
												label='Bắt buộc chọn đơn'
												sx={{ height: 22, background: '#E8F5E9', color: mainGreen }}
											/>
										</Stack>
									</MenuItem>
								))}
								{otherExpenseCategories.length > 0 && (
									<ListSubheader disableSticky sx={categoryGroupSx('other')}>
										Chi khác
									</ListSubheader>
								)}
								{otherExpenseCategories.map((category) => (
									<MenuItem key={category._id} value={category._id}>
										<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
											<Typography>{category.name}</Typography>
											<Stack direction='row' spacing={0.5}>
												{categoryAllowedSources(category).length === 1 && (
													<Chip
														size='small'
														label={`Chỉ ${expenseSourceLabel(categoryAllowedSources(category)[0]).toLowerCase()}`}
														sx={{ height: 22, background: '#EDE7F6', color: '#5E35B1' }}
													/>
												)}
												{categoryAllowsOrders(category) && (
													<Chip
														size='small'
														label='Chọn đơn'
														sx={{ height: 22, background: '#E3F2FD', color: mainBlue }}
													/>
												)}
												{category.requiresApproval && (
													<Chip
														size='small'
														label='Cần xác nhận'
														sx={{ height: 22, background: '#FFF3E0', color: '#B76E00' }}
													/>
												)}
												<Chip size='small' label='Khác' sx={{ height: 22, background: '#FFF8E1', color: '#B76E00' }} />
											</Stack>
										</Stack>
									</MenuItem>
								))}
								{cashoutExpenseCategories.length > 0 && (
									<ListSubheader disableSticky sx={categoryGroupSx('cashout')}>
										Rút tiền
									</ListSubheader>
								)}
								{cashoutExpenseCategories.map((category) => (
									<MenuItem key={category._id} value={category._id}>
										<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
											<Typography>{category.name}</Typography>
											<Stack direction='row' spacing={0.5}>
												{category.requiresApproval && (
													<Chip
														size='small'
														label='Cần xác nhận'
														sx={{ height: 22, background: '#FFF3E0', color: '#B76E00' }}
													/>
												)}
												<Chip
													size='small'
													label='Không vào tổng chi'
													sx={{ height: 22, background: '#FDE7EF', color: '#9C1C4A' }}
												/>
											</Stack>
										</Stack>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					<Box>
						<TextField
							fullWidth
							size='small'
							label='Nội dung'
							multiline
							minRows={2}
							value={expenseForm.content}
							onChange={(event) => setExpenseForm((prev) => ({ ...prev, content: event.target.value }))}
						/>
					</Box>
					{canPickOrders && (
						<Box>
							<Autocomplete
								multiple
								disableCloseOnSelect
								fullWidth
								limitTags={5}
								size='small'
								slots={{ popper: OrderPickerPopper }}
								options={expenseOrderOptions}
								value={selectedExpenseOrders}
								sx={{
									width: '100%',
									'& .MuiAutocomplete-inputRoot': {
										alignItems: 'flex-start',
										minHeight: 48,
										py: 0.5,
										pr: '52px !important',
									},
									'& .MuiAutocomplete-tag': {
										maxWidth: 180,
									},
								}}
								isOptionEqualToValue={(option, value) => option._id === value._id}
								getOptionLabel={(order) => {
									const shipText =
										parseMoney(order.shippingTotal) > 0 ? formatMoney(order.shippingTotal) : 'Không thu ship'
									return `${orderCode(order)} - ${shipText} - ${order.payment === 'transfer' ? 'CK' : 'TM'}`
								}}
								getOptionDisabled={(order) =>
									Boolean(getLockedOrderExpense(order._id, expenseForm.type, expenseForm.category))
								}
								onChange={(_event, value) => {
									setExpenseForm((prev) => ({ ...prev, orderIds: value.map((order) => order._id) }))
								}}
								noOptionsText='Không có đơn hoàn thành trong ngày'
								renderTags={(value, getTagProps) =>
									value.map((order, index) => {
										const { key, ...tagProps } = getTagProps({ index })
										return (
											<Chip
												key={key}
												size='small'
												label={`${orderCode(order)}${
													parseMoney(order.shippingTotal) > 0 ? ` - ${formatMoney(order.shippingTotal)}` : ''
												}`}
												{...tagProps}
											/>
										)
									})
								}
								renderOption={(props, order, { selected }) => {
									const { key, ...optionProps } = props
									const lockedExpense = getLockedOrderExpense(order._id, expenseForm.type, expenseForm.category)
									const contactText = orderContactText(order)
									const itemsText = orderItemsText(order)
									const quantityText = Number(order.quantity) > 0 ? `${order.quantity}kg` : ''
									const noteText = order.customerNote || order.detail
									return (
										<Box
											component='li'
											key={key}
											{...optionProps}
											className={`${optionProps.className || ''} cashflow-order-option`}
											sx={{
												...orderAssignmentRowSx(
													order,
													false,
													expenseOrderOptions.findIndex((item) => item._id === order._id),
												),
												alignItems: 'flex-start !important',
												py: 1.25,
												px: 1.5,
											}}>
											<Checkbox checked={selected} sx={{ mr: 1 }} />
											<Box sx={{ minWidth: 0, flex: 1 }}>
												<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
													<Typography sx={{ fontWeight: 800 }}>{orderCode(order)}</Typography>
													<Chip size='small' label={order.payment === 'transfer' ? 'CK' : 'TM'} sx={{ height: 22 }} />
													{quantityText && <Chip size='small' label={quantityText} sx={{ height: 22 }} />}
													{order.app && <Chip size='small' color='info' label='App' sx={{ height: 22 }} />}
													{order.quickPurchase && (
														<Chip size='small' color='error' label='Mua nhanh' sx={{ height: 22 }} />
													)}
													{order.edited?.value && <Chip size='small' label='Đã sửa' sx={{ height: 22 }} />}
													<Typography sx={{ color: parseMoney(order.shippingTotal) > 0 ? mainGreen : '#637381' }}>
														{parseMoney(order.shippingTotal) > 0 ? formatMoney(order.shippingTotal) : 'Không thu ship'}
													</Typography>
													{lockedExpense && (
														<Chip
															size='small'
															color='warning'
															label={expenseForm.type === 'shipping' ? 'Đã có chi ship' : 'Đã dùng'}
															sx={{ height: 22 }}
														/>
													)}
												</Stack>
												{itemsText && (
													<Typography sx={{ mt: 0.5, fontSize: 12, color: '#263238', fontWeight: 700 }}>
														{itemsText}
													</Typography>
												)}
												{order.tag?.length > 0 && (
													<Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
														{order.tag.map((tag) => (
															<Chip
																key={`${order._id}-${tag.name}`}
																size='small'
																label={tag.name}
																sx={{
																	height: 22,
																	background: tag.color || '#ECEFF1',
																	color: '#fff',
																	fontWeight: 700,
																}}
															/>
														))}
													</Stack>
												)}
												<Typography sx={{ fontSize: 12, color: '#637381' }}>
													{formatMoney(order.totalPrice)} {contactText}
												</Typography>
												{noteText && (
													<Typography
														component='div'
														sx={{
															mt: 0.5,
															color: '#455A64',
															fontSize: 12,
															lineHeight: 1.45,
															display: '-webkit-box',
															WebkitLineClamp: 2,
															WebkitBoxOrient: 'vertical',
															overflow: 'hidden',
															'& a': { color: mainBlue, fontWeight: 700 },
														}}
														dangerouslySetInnerHTML={{ __html: transformDetailWithPhone(noteText) }}
													/>
												)}
											</Box>
										</Box>
									)
								}}
								renderInput={(params) => <TextField {...params} label='Đơn liên quan' placeholder='Tìm mã đơn' />}
							/>
							{expenseForm.orderIds.length > 0 && (
								<Typography sx={{ color: '#637381', fontSize: 13, mt: 1 }}>
									{isShippingExpense
										? `Đã chọn ${expenseForm.orderIds.length} đơn - Thu ship ${formatMoney(selectedShippingIncome)}`
										: `Đã liên kết ${expenseForm.orderIds.length} đơn`}
								</Typography>
							)}
						</Box>
					)}
					<Box>
						<Button component='label' variant='outlined' startIcon={<UploadFileIcon />}>
							{expenseForm.photos?.length > 0 ? `${expenseForm.photos.length} ảnh mới` : 'Đính kèm ảnh'}
							<input
								hidden
								multiple
								type='file'
								accept='image/*'
								onChange={(event) => setExpenseForm((prev) => ({ ...prev, photos: event.target.files }))}
							/>
						</Button>
						{expenseForm.existingPhotos?.length > 0 && (
							<Typography sx={{ display: 'inline-block', ml: 1, color: '#637381', fontSize: 13 }}>
								Đang giữ {expenseForm.existingPhotos.length} ảnh cũ
							</Typography>
						)}
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button startIcon={<CloseIcon />} onClick={closeExpenseDialog}>
					Hủy
				</Button>
				<Button
					variant='contained'
					startIcon={<SaveIcon />}
					disabled={!canManageExpense}
					sx={{ background: mainGreen }}
					onClick={saveExpense}>
					{editingExpenseId ? 'Cập nhật khoản chi' : 'Lưu khoản chi'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
